import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from '../components/PrimaryButton';
import FloatingInput from '../components/FloatingInput';
import PasswordResetView from '../components/PasswordResetView';
import peopleIllustration from '../assets/img/illustrations/people-illu.png';
import dingoLogo from '../assets/img/logo/dingo-logo.png';
import googleIcon from '../assets/img/icons/google-ic.png';
import { useLoginViewModel } from '../viewmodels/LoginViewModel';
import { LoginFormState } from '../models/User';
import { useAuth } from '../contexts/AuthContext';
import { loginWithGoogleIdToken } from '../api/services/googleAuthService';
import { loginWithAppleIdToken } from '../api/services/appleAuthService';
import { signInWithApple, isFirebaseConfigured } from '../firebase';

declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141 4 184.8 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const Login = () => {
  const { state, updateField, handleSubmit, isFormValid, resetState, setState } = useLoginViewModel();
  const { login: authLogin, logoutReason } = useAuth();
  const navigate = useNavigate();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [appleError, setAppleError] = useState('');
  const [appleLoading, setAppleLoading] = useState(false);

  const handleAppleSignIn = async () => {
    setAppleError('');
    setAppleLoading(true);
    try {
      const idToken = await signInWithApple();
      const data = await loginWithAppleIdToken(idToken);
      authLogin(data.token, data.user);
      navigate('/dashboard');
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user') {
        setAppleLoading(false);
        return;
      }
      setAppleError(err.message || 'Connexion Apple impossible.');
    } finally {
      setAppleLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const handleCredential = async (response: { credential: string }) => {
      setGoogleError('');
      try {
        const data = await loginWithGoogleIdToken(response.credential);
        authLogin(data.token, data.user);
        navigate('/dashboard');
      } catch (err: any) {
        setGoogleError(err.message || 'Connexion Google impossible.');
      }
    };

    const renderButton = () => {
      if (!window.google || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential
      });
      // Bouton Google natif rendu invisible mais toujours cliquable, superposé
      // pile sur notre PrimaryButton stylé — on garde notre propre visuel
      // tout en déclenchant le vrai flow Google (impossible à re-styler
      // directement, Google impose son propre rendu dans une iframe).
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: 400,
        text: 'continue_with'
      });
    };

    if (window.google) {
      renderButton();
      return;
    }

    const existing = document.getElementById('google-identity-script');
    if (existing) {
      existing.addEventListener('load', renderButton);
      return () => existing.removeEventListener('load', renderButton);
    }

    const script = document.createElement('script');
    script.id = 'google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasswordResetClick = () => {
    // Réinitialiser l'état du formulaire de connexion quand on ouvre la modal
    resetState();
    // S'assurer que le message est effacé
    setState((prevState: LoginFormState) => ({
      ...prevState,
      message: '',
      errors: [],
      success: false
    }));
    setShowPasswordReset(true);
  };

  return (
    <div className="min-h-screen h-screen flex flex-col md:flex-row w-full bg-gray-50 overflow-hidden min-h-0">
      {/* Partie gauche (Formulaire) */}
      <div className="md:w-1/2 w-full flex flex-col justify-start items-center p-4 md:p-12 bg-white h-screen overflow-y-auto pt-8">
        <div className="mb-8 text-center">
          <img src={dingoLogo} alt="Dingo" className="h-12 mb-6 mx-auto" />
          <h1 className="text-3xl font-bold mb-2 text-indigo-600">Espace équipe Dingo</h1>
          <p className="text-gray-500">Réservé au staff — connectez-vous à votre compte</p>
        </div>
        
        {/* Pourquoi on est ici sans l'avoir demandé : une session expirée
            renvoyait sur cet écran sans un mot, et l'admin croyait à un bug. */}
        {logoutReason && !state.message && !showPasswordReset && (
          <div className="w-full p-3 rounded-md mb-4 text-center bg-amber-50 text-amber-800 border border-amber-200">
            {logoutReason}
          </div>
        )}

        {/* Messages d'erreur/succès */}
        {state.message && !showPasswordReset && (
          <div className={`w-full p-3 rounded-md mb-4 text-center ${
            state.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {state.message}
          </div>
        )}
        <form className="space-y-5 w-full" onSubmit={handleFormSubmit}>
          <FloatingInput
            label="Adresse email"
            name="email"
            type="email"
            placeholder="debra.holt@example.com"
            value={state.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('email', e.target.value)}
          />
          <FloatingInput
            label="Mot de passe"
            name="password"
            type="password"
            placeholder="********"
            value={state.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('password', e.target.value)}
          />
          <div className="flex items-center justify-end text-sm">
            <button 
              type="button"
              onClick={handlePasswordResetClick}
              className="text-indigo-600 hover:underline font-medium"
            >
              Mot de passe oublié ?
            </button>
          </div>
          <PrimaryButton
            fullWidth
            type="submit"
            className="mt-2"
            rounded="rounded-md"
            bgColor="bg-indigo-600"
            disabled={!isFormValid() || state.isLoading}
          >
            {state.isLoading ? 'Connexion en cours...' : 'Se connecter'}
          </PrimaryButton>

          {GOOGLE_CLIENT_ID && (
            <div className="relative w-full">
              <PrimaryButton
                fullWidth
                type="button"
                variant="outline"
                className="mt-0"
                icon={<img src={googleIcon} alt="Google" className="w-5 h-5" />}
                iconPosition="left"
                rounded="rounded-md"
              >
                Se connecter avec Google
              </PrimaryButton>
              {/* Vrai bouton Google, invisible, superposé pour recevoir le clic */}
              <div ref={googleButtonRef} className="absolute inset-0 overflow-hidden opacity-0" />
            </div>
          )}

          {isFirebaseConfigured && (
            <PrimaryButton
              fullWidth
              type="button"
              variant="outline"
              className="mt-0"
              icon={<AppleIcon />}
              iconPosition="left"
              rounded="rounded-md"
              onClick={handleAppleSignIn}
              disabled={appleLoading}
            >
              {appleLoading ? 'Connexion...' : 'Se connecter avec Apple'}
            </PrimaryButton>
          )}

          {(googleError || appleError) && (
            <div className="w-full p-3 rounded-md text-center bg-red-100 text-red-700 text-sm">
              {googleError || appleError}
            </div>
          )}
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Prêt à rouler librement ? <a href="/signup" onClick={(e) => { e.preventDefault(); navigate('/signup'); }} className="text-indigo-600 hover:underline font-medium cursor-pointer">Rejoignez l'aventure !</a>
        </div>
      </div>
      {/* Partie droite (Illustration) */}
      <div className="hidden md:flex md:w-1/2 w-full bg-indigo-500 relative flex-col justify-center items-center p-8 md:p-12 text-white min-h-screen h-full">
        <div className="absolute left-0 bottom-0 w-40 h-40 bg-indigo-300 opacity-30 rounded-full -mb-20 -ml-20 z-0" />
        <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-200 opacity-20 rounded-full -mt-16 -mr-16 z-0" />
        <div className="relative z-10 w-full flex flex-col items-center">
          <img
            src={peopleIllustration}
            alt="People illustration"
            className="w-4/5 max-w-md object-contain drop-shadow-2xl"
          />
        </div>
      </div>
      
      {showPasswordReset && (
        <PasswordResetView onClose={() => {
          setShowPasswordReset(false);
          // Réinitialiser l'état quand on ferme le modal
          resetState();
        }} />
      )}
    </div>
  );
};

export default Login;
